import { FormattedMessage, useIntl } from "react-intl";
import { FC, useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import { useUpdateProductShippingCostMutation } from "../../../api/productsAPI";
import { TProduct } from "../../../api/api.types";

type UpdateProductShippingCostProps = {
	onHide: () => void;
	product: TProduct;
};

const UpdateProductShippingCost: FC<UpdateProductShippingCostProps> = ({
	onHide,
	product,
}) => {
	const [chinaShippingCost, setChinaShippingCost] = useState<number>(0);
	const [isUseCustom, setIsUseCustom] = useState<boolean>(true);
	const [isChinaShippingAllowed, setIsChinaShippingAllowed] =
		useState<boolean>(false);
	const [shippingCost, setShippingCost] = useState<number>(0);

	const intl = useIntl();

	const [editShippingCost] = useUpdateProductShippingCostMutation();

	const handleSaveShippingCost = () => {
		editShippingCost({
			id: product.id,
			shipping_cost: isUseCustom ? shippingCost * 100 : null,
			china_shipping_cost: isChinaShippingAllowed
				? chinaShippingCost * 100
				: 0,
			is_china_shipping_allowed: isChinaShippingAllowed,
		}).then((res) => {
			if ("error" in res) {
				const error = (res.error as { data: { error: string[] } })?.data
					?.error;

				if (error && error?.length > 0) {
					toast.error(error[0]);
				} else {
					toast.error(
						intl.formatMessage({
							id: "anErrorOccurredWhileUpdatingShippingCost",
						})
					);
				}
			} else {
				onHide();
				toast.success(
					intl.formatMessage({
						id: "shippingCostUpdatedSuccessfully",
					})
				);
			}
		});
	};

	useEffect(() => {
		if (product) {
			setIsUseCustom(product.shipping_price !== null ? true : false);
			setShippingCost(
				product.shipping_price !== null
					? product.shipping_price / 100
					: 0
			);
			setChinaShippingCost(
				product.china_shipping_price
					? product.china_shipping_price / 100
					: 0
			);
			setIsChinaShippingAllowed(product.is_china_shipping_allowed);
		} else {
			setIsUseCustom(false);
			setShippingCost(0);
			setChinaShippingCost(0);
			setIsChinaShippingAllowed(false);
		}
	}, [product]);

	return (
		<Modal className="modal-500" centered show={true} onHide={onHide}>
			<Modal.Header className="modal__header" closeButton>
				<Modal.Title
					style={{ textTransform: "capitalize" }}
					className="modal__title"
				>
					<FormattedMessage id="changeShippingDetails" />
				</Modal.Title>
			</Modal.Header>
			<Modal.Body
				style={{ gap: "12px" }}
				className="pt-0 d-flex flex-column"
			>
				<Form.Group>
					<Form.Label className="default-input-label">
						<FormattedMessage id="customShippingCost" />
					</Form.Label>
					<Form.Control
						style={
							!isUseCustom
								? {
										backgroundColor: "#f0f0f0",
										opacity: 0.5,
										cursor: "default",
									}
								: {}
						}
						onChange={(e) =>
							setShippingCost(Number(e.target.value))
						}
						placeholder={intl.formatMessage({
							id: "shippingCost",
						})}
						className="default-input"
						readOnly={!isUseCustom}
						value={shippingCost}
						type="number"
					/>
				</Form.Group>
				<Form.Group className="d-flex align-items-center gap-1">
					<Form.Check
						onChange={(e) => setIsUseCustom(e.target.checked)}
						checked={isUseCustom}
					/>
					<Form.Label className="default-input-label mb-0">
						<FormattedMessage id="useCustomShippingCost" />
					</Form.Label>
				</Form.Group>
				<Form.Group>
					<Form.Label className="default-input-label">
						<FormattedMessage id="chinaShippingCost" />
					</Form.Label>
					<Form.Control
						style={
							!isChinaShippingAllowed
								? {
										backgroundColor: "#f0f0f0",
										opacity: 0.5,
										cursor: "default",
									}
								: {}
						}
						onChange={(e) =>
							setChinaShippingCost(Number(e.target.value))
						}
						readOnly={!isChinaShippingAllowed}
						placeholder={intl.formatMessage({
							id: "chinaShippingCost",
						})}
						className="default-input"
						value={chinaShippingCost}
						type="number"
					/>
				</Form.Group>
				<Form.Group className="d-flex align-items-center gap-1">
					<Form.Check
						onChange={(e) =>
							setIsChinaShippingAllowed(e.target.checked)
						}
						checked={isChinaShippingAllowed}
					/>
					<Form.Label className="default-input-label mb-0">
						<FormattedMessage id="allowShippingToChina" />
					</Form.Label>
				</Form.Group>
				<div className="d-flex align-items-center justify-content-end mt-2">
					<button
						onClick={handleSaveShippingCost}
						className="ice-button w-100"
					>
						<FormattedMessage id="save" />
					</button>
				</div>
			</Modal.Body>
		</Modal>
	);
};

export default UpdateProductShippingCost;
